import {ComputedBatteryResponse, IVoltageTable, IVoltageValues} from "contracts/IBatteryValues";

export function getVoltageValuesForDevice(device_model: string): IVoltageValues {
    const voltageValues = {
        'default': {
            minVoltage: 2.5,
            maxVoltage: 6.7,
        },
        'atrack': {
            minVoltage: 2.2,
            maxVoltage: 6.2,
        },
        'atrack-as-11': {
            minVoltage: 2.6,
            maxVoltage: 3.2,
        },
    }

    // Check if the device model is in the voltage values
    if (voltageValues[device_model]) {
        return voltageValues[device_model];
    } else {
        // If not, try to find if the first segment of the device model is in the voltage values
        const manufacturer = device_model?.split('-')[0] ?? 'default';
        if (voltageValues[manufacturer]) {
            return voltageValues[manufacturer];
        }

        return voltageValues['default'];
    }
}

export function getExternalVoltageTableForDevice(device_model: string): Array<IVoltageTable> {
    const voltageTables = {
        'queclink-tmr-100-mg': [
            {voltage: 3.5, percentage: 0},
            {voltage: 3.515, percentage: 1},
            {voltage: 3.565, percentage: 2},
            {voltage: 3.593, percentage: 3},
            {voltage: 3.615, percentage: 4},
            {voltage: 3.626, percentage: 5},
            {voltage: 3.644, percentage: 10},
            {voltage: 3.663, percentage: 14},
            {voltage: 3.694, percentage: 20},
            {voltage: 3.715, percentage: 27},
            {voltage: 3.73, percentage: 32},
            {voltage: 3.742, percentage: 37},
            {voltage: 3.751, percentage: 40},
            {voltage: 3.764, percentage: 45},
            {voltage: 3.786, percentage: 52},
            {voltage: 3.806, percentage: 57},
            {voltage: 3.807, percentage: 57},
            {voltage: 3.814, percentage: 59},
            {voltage: 3.819, percentage: 60},
            {voltage: 3.82, percentage: 60},
            {voltage: 3.826, percentage: 61},
            {voltage: 3.831, percentage: 62},
            {voltage: 3.835, percentage: 62},
            {voltage: 3.847, percentage: 64},
            {voltage: 3.859, percentage: 66},
            {voltage: 3.87, percentage: 68},
            {voltage: 3.881, percentage: 70},
            {voltage: 3.892, percentage: 72},
            {voltage: 3.903, percentage: 73},
            {voltage: 3.912, percentage: 75},
            {voltage: 3.927, percentage: 78},
            {voltage: 3.938, percentage: 80},
            {voltage: 3.949, percentage: 81},
            {voltage: 3.968, percentage: 83},
            {voltage: 3.987, percentage: 85},
            {voltage: 4.001, percentage: 87},
            {voltage: 4.011, percentage: 88},
            {voltage: 4.023, percentage: 90},
            {voltage: 4.046, percentage: 93},
            {voltage: 4.062, percentage: 95},
            {voltage: 4.081, percentage: 97},
            {voltage: 4.1, percentage: 100},
        ],
        'barra-gps': [
            {voltage: 3.6, percentage: 100},
            {voltage: 3.4, percentage: 90},
            {voltage: 3.3, percentage: 80},
            {voltage: 3.2, percentage: 70},
            {voltage: 3.1, percentage: 60},
            {voltage: 3.0, percentage: 50},
            {voltage: 2.9, percentage: 40},
            {voltage: 2.8, percentage: 30},
            {voltage: 2.6, percentage: 20},
            {voltage: 2.4, percentage: 10},
            {voltage: 2.2, percentage: 0},
        ],

        //INFO: this is based on the default voltage table may be from a calamp device
        'default': [
            {voltage: 12.7, percentage: 100},
            {voltage: 12.5, percentage: 90},
            {voltage: 12.42, percentage: 80},
            {voltage: 12.32, percentage: 70},
            {voltage: 12.2, percentage: 60},
            {voltage: 12.06, percentage: 50},
            {voltage: 11.9, percentage: 40},
            {voltage: 11.79, percentage: 30},
            {voltage: 11.58, percentage: 20},
            {voltage: 11.31, percentage: 10},
            {voltage: 10.5, percentage: 0},
        ]
    }

    // Check if the device model is in the voltage tables
    if (voltageTables[device_model]) {
        return voltageTables[device_model];
    } else {
        // If not, try to find if the first segment of the device model is in the voltage tables
        const manufacturer = device_model?.split('-')[0] ?? 'default';

        if (voltageTables[manufacturer]) {
            return voltageTables[manufacturer];
        }
        // If not, return the default voltage table
        return voltageTables['default'];
    }
}


export function computeInternalBatteryVoltageFromLevel(battery_level: number): ComputedBatteryResponse {
    try {
        if (battery_level) return parseFloat(((battery_level / 100 + 5.52) / 1.23).toFixed(2));
        else return null;
    } catch (e) {
        return null;
    }
}

export function calculateBatteryLevelFromInternalVoltage(voltageFromDevice: number | string, deviceModel: string): ComputedBatteryResponse {
    const computeValues = getVoltageValuesForDevice(deviceModel);
    const minVoltage = computeValues.minVoltage;
    const maxVoltage = computeValues.maxVoltage;
    voltageFromDevice = (typeof voltageFromDevice === "string") ? parseFloat(voltageFromDevice) : voltageFromDevice;
    let result: number;
    if (voltageFromDevice <= minVoltage) {
        result = 0;
    } else if (voltageFromDevice >= maxVoltage) {
        result = 100;
    } else {
        result = ((voltageFromDevice - minVoltage) / (maxVoltage - minVoltage)) * 100;
    }
    return parseFloat(result.toFixed(2));
}


export function calculateBatteryPercentageFromExternalVoltage(voltageFromDevice: string, device_model: string): ComputedBatteryResponse {
    const voltage = parseFloat(voltageFromDevice);
    try {
        // Choose the correct voltage table
        const voltageTable = getExternalVoltageTableForDevice(device_model);

        // Handle edge cases
        const maxVoltage = voltageTable[0].voltage;
        const minVoltage = voltageTable[voltageTable.length - 1].voltage;

        if (voltage >= maxVoltage) return 100;
        if (voltage <= minVoltage) return 0;

        // Find the two closest voltage entries
        for (let i = 0; i < voltageTable.length - 1; i++) {
            if (voltage <= voltageTable[i].voltage && voltage > voltageTable[i + 1].voltage) {
                const higherVoltage = voltageTable[i];
                const lowerVoltage = voltageTable[i + 1];

                // Interpolate between the two closest percentages
                const voltageRange = higherVoltage.voltage - lowerVoltage.voltage;
                const percentageRange = higherVoltage.percentage - lowerVoltage.percentage;
                const voltageAboveLower = voltage - lowerVoltage.voltage;

                const interpolatedPercentage =
                    lowerVoltage.percentage + (voltageAboveLower / voltageRange) * percentageRange;

                return Math.round(interpolatedPercentage);
            }
        }
    } catch (e) {
        return null;
    }
}
